import { ALLOW_AVAILABILITY_FALLBACK, resolveLocationCensus, scoreAdjustedRequiredForLocation } from "../../../utils/censusResolution";
import type { DemoRequiredInput } from "../../../utils/hospitalDemoScore";
import type { DepartmentLocation, StatsMode } from "../../../utils/models";

/**
 * For ratio-ladder facilities, recompute each location's `amountRequired` from the day's census via
 * the canonical resolver, so the printout agrees with the daily and weekly gap views by construction.
 *
 * Replaces locations' static `amountRequired` with the census-aware value used downstream by the
 * gap-finder and by role-group stats. `{ kind: "all" }` because a printout row is the location TOTAL
 * across every staffing dimension, not one role group's share.
 *
 * `demoRequired` is the demo-only adjustment (patient score or node split), or `null` for everyone else.
 */
export const applyRatioLadderToLocations = (
	locations: DepartmentLocation[],
	byDimensions: Record<string, number>,
	totalCensus: number,
	dayAbbr: string,
	deptStatsMode: StatsMode,
	demoRequired: DemoRequiredInput,
): DepartmentLocation[] => {
	return locations.map<DepartmentLocation>((location) => {
		const locationCensus = resolveLocationCensus(location, byDimensions, totalCensus, deptStatsMode, ALLOW_AVAILABILITY_FALLBACK);
		// Demo-adjusted so the printout asks for the same number as Staffing.
		const amountRequired = scoreAdjustedRequiredForLocation(location, locationCensus, deptStatsMode, dayAbbr, { kind: "all" }, demoRequired);
		return { ...location, amountRequired };
	});
};

/**
 * Map each location NAME → its rounded resolved census, for the "show location census" printout
 * labels. Uses the canonical resolver, so a label always matches the number the gap views used.
 * Several locations can share one name while mapping differently; the label takes the widest of them,
 * so it does not depend on the order `department.locations` happens to arrive in.
 *
 * Returns an empty map when the day reports no dimensions at all, so callers render location names
 * unchanged rather than labelling every one of them "0".
 */
export const buildLocationCensusByName = (
	deptLocations: DepartmentLocation[],
	byDimensions: Record<string, number>,
	totalCensus: number,
	deptStatsMode: StatsMode,
): Map<string, number> => {
	const byName = new Map<string, number>();
	if (Object.keys(byDimensions).length === 0) return byName;
	for (const location of deptLocations) {
		// A name is a label, not a mapping: same-named locations can cover different areas, so the label
		// has no single answer. Take the WIDEST, the same rule `censusDisplayGroups.partsForAutoGroup`
		// uses for the same problem — the areas may overlap, so a sum can exceed the department census,
		// whereas the widest can only under-state a genuinely disjoint group.
		//
		// "First variant above zero wins" was order-dependent and the locations query has no ORDER BY.
		// It was masked while an unmapped location resolved to 0 (a zero never stuck, so a mapped
		// sibling always overwrote it); now that an unmapped location resolves to the department total,
		// whichever row arrived first would decide the label.
		const resolved = Math.round(resolveLocationCensus(location, byDimensions, totalCensus, deptStatsMode, ALLOW_AVAILABILITY_FALLBACK));
		const existing = byName.get(location.location);
		byName.set(location.location, existing === undefined ? resolved : Math.max(existing, resolved));
	}
	return byName;
};
